import { callBackend } from './api';
import { runOptimizerWithQueue } from './optimizerQueue';
import {
    buildOptimizerConstraintDocument,
    canonicalJsonObject,
    decodeOptimizerConstraintDocument,
} from './optimizerConstraintEditor';

export const OPTIMIZER_V2_RECEIPT_CONTRACT = 'optimizer-run-receipt.v2';
export const OPTIMIZER_V6_RECEIPT_CONTRACT = 'optimizer-run-receipt.v6';
export const OPTIMIZER_V7_RECEIPT_CONTRACT = 'optimizer-run-receipt.v7';
export const OPTIMIZER_V8_RECEIPT_CONTRACT = 'optimizer-run-receipt.v8';
export const OPTIMIZER_V9_RECEIPT_CONTRACT = 'optimizer-run-receipt.v9';
// The receipt's result projection. v2 carries no Sector fields; earlier results
// were reset and are rejected, never read.
export const OPTIMIZER_RUN_RESULT_CONTRACT = 'optimizer-run-result.v2';
// Every receipt contract a saved Optimizer portfolio may carry.
export const OPTIMIZER_SAVED_RECEIPT_CONTRACTS = Object.freeze([
    OPTIMIZER_V6_RECEIPT_CONTRACT,
    OPTIMIZER_V7_RECEIPT_CONTRACT,
    OPTIMIZER_V8_RECEIPT_CONTRACT,
    OPTIMIZER_V9_RECEIPT_CONTRACT,
]);
export const OPTIMIZER_V2_CONSTRAINT_SCHEMA = 'optimizer-constraints.v2';
export const OPTIMIZER_V2_PROVIDER_SOURCE = 'deed325cc997cc11e922c5294f7d2e33ceba92fd';
export const OPTIMIZER_V4_CONSTRAINT_SCHEMA = 'optimizer-constraints.v4';
export const OPTIMIZER_V4_CONSTRAINT_DIGEST = '44ea7a1dffa25dda75af7e01e41d063a930aaeda853a4f6f61b9b0eb8b6bc9b0';
const OPTIMIZER_V4_PROVIDER_DIGEST = 'ff930363505f82cfb6f8010f090610bf5d6c34fde0973f5952a3410182b5757b';
const OPTIMIZER_V4_PROVIDER_ROUTE = 'in-process:optimizer-v4';
export const OPTIMIZER_V5_CONSTRAINT_SCHEMA = 'optimizer-constraints.v5';
export const OPTIMIZER_V5_CONSTRAINT_DIGEST = '3ccbc1a0097d1c1f2d8e4763f9f8376840f21b7cfbe3ec558fba1f5993772f69';
const OPTIMIZER_V5_PROVIDER_CONTRACT = 'optimizer-provider.v5';
const OPTIMIZER_V5_PROVIDER_DIGEST = '53f792f3da4b987d561acccd822c2b36cda5a3a59c2dc87f3a72d16471a932bb';
const OPTIMIZER_V5_PROVIDER_ROUTE = 'in-process:optimizer-v5';
// Max Sharpe within the total volatility limit (provider v7, v9 receipts)
// keeps the v5 limits.
export const OPTIMIZER_V7_PROVIDER_DIGEST = '2630895e57863189b7d6763cf5be64cbbf7a71ee91be863af4349b8c42a4ad9c';
export const OPTIMIZER_V7_PROVIDER_ROUTE = 'in-process:optimizer-sharpe-v1';
export const OPTIMIZER_V7_PROVIDER_MODEL_BUILD = 'optimizer-sharpe-risk-budget.v1';
// The v5 trading rules in wire order, with the editor field and default limit of
// each. A new rule starts switched off.
export const OPTIMIZER_TRADING_RULES = Object.freeze([
    Object.freeze({ name: 'max_turnover_pct_gmv', field: 'turnover_pct_gmv', defaultValue: 20 }),
    Object.freeze({ name: 'max_position_days_adv', field: 'position_days_adv', defaultValue: 1 }),
    Object.freeze({ name: 'max_trade_pct_adv', field: 'trade_pct_adv', defaultValue: 10 }),
]);
export const OPTIMIZER_SECURITY_CONTROLS = Object.freeze(['hold', 'no_short']);
export const OPTIMIZER_V2_PORTFOLIO_PROJECTION = 'optimizer-portfolio-projection.v2';
export const OPTIMIZER_WATCHLIST_COMPATIBILITY_CONTRACT = 'optimizer-watchlist-compatibility.v1';
export const OPTIMIZER_CONTEXT_STATUS_V1_CONTRACT = 'optimizer-context-status.v1';
export const OPTIMIZER_CONTEXT_STATUS_V2_CONTRACT = 'optimizer-context-status.v2';
export const OPTIMIZER_CONTEXT_STATUS_CONTRACT = OPTIMIZER_CONTEXT_STATUS_V1_CONTRACT;
export const OPTIMIZER_SAVED_INPUT_REPLAY_CONTRACT = 'optimizer-saved-input-replay.v1';
export const OPTIMIZER_ATTRIBUTE_RISK_RESULTS_CONTRACT = (
    'optimizer-attribute-risk-results.v1'
);
export const OPTIMIZER_CONSTRAINT_EVALUATION_CONTRACT = (
    'optimizer-constraint-evaluation.v1'
);
const CONSTRAINT_EVALUATION_SCOPES = Object.freeze(['portfolio', 'factor', 'attribute', 'design']);
const CONSTRAINT_EVALUATION_FORMS = Object.freeze(['max', 'abs_max']);
const CONSTRAINT_EVALUATION_STATUSES = Object.freeze([
    'within',
    'at_limit',
    'exceeds',
    'not_applied',
    'not_configured',
    'unavailable',
]);
export const OPTIMIZER_GROUP_METRICS = Object.freeze([
    {
        value: 'group_beta_exposure_abs_pct_gmv',
        label: 'β exposure, % of GMV',
    },
    {
        value: 'group_net_market_value_abs_pct_gmv',
        label: 'NMV, % of GMV',
    },
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const UUID4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SHA_RE = /^[0-9a-f]{64}$/;
const ENVIRONMENTS = { dev: 'dev', uat: 'uat', production: 'production' };
const PROVIDER_PROVENANCE_KEYS = Object.freeze([
    'service',
    'environment',
    'source_revision',
    'deployed_revision',
    'image_digest',
    'route',
    'model_build',
    'solver_name',
    'solver_version',
    'contract_digest',
    'constraint_schema_version',
    'constraint_contract_digest',
]);
const RESULT_SECURITY_KEYS = Object.freeze([
    'beta',
    'cognive_id',
    'expected_return',
    'gmv_actual',
    'gmv_optimized',
    'score',
    'ticker',
]);
const RISK_METRIC_KEYS = Object.freeze(['act_port', 'opt_port', 'delta_port']);
const CURRENT_POSITION_KEYS = Object.freeze([
    'cognive_id',
    'gmv',
    'quantity',
    'ticker',
]);

function requiredText(value, label) {
    if (typeof value !== 'string' || !value || value !== value.trim()) {
        throw new Error(`${label} is invalid.`);
    }
    return value;
}

function requireOptimizerWatchlistCompatibility(value, { details = false } = {}) {
    const partial = value?.status === 'partially_available';
    const counts = [
        value?.saved_security_count,
        value?.available_security_count,
        value?.missing_security_count,
    ];
    if (
        value?.contract !== OPTIMIZER_WATCHLIST_COMPATIBILITY_CONTRACT
        || !['compatible', 'partially_available'].includes(value?.status)
        || value?.reason !== (partial ? 'watchlist_membership_changed' : null)
        || !UUID_RE.test(value?.current_watchlist_revision_id ?? '')
        || (value?.source_watchlist_revision_id !== null
            && !UUID_RE.test(value.source_watchlist_revision_id ?? ''))
        || !['preview_required', 'source_revision_unavailable'].includes(
            value?.restoration_status,
        )
        || value?.restoration_status !== (
            value?.source_watchlist_revision_id === null
                ? 'source_revision_unavailable'
                : 'preview_required'
        )
        || counts.some((count) => !Number.isInteger(count) || count < 0)
        || value.available_security_count + value.missing_security_count
            !== value.saved_security_count
        || partial !== (value.missing_security_count > 0)
    ) {
        throw new Error('Optimizer Watchlist compatibility was invalid.');
    }
    if (details) {
        const available = value.available_cognive_ids;
        const missing = value.missing_cognive_ids;
        if (
            !Array.isArray(available)
            || !Array.isArray(missing)
            || available.length !== value.available_security_count
            || missing.length !== value.missing_security_count
            || [...available, ...missing].some((id) => !requiredText(id, 'Cognive ID'))
            || new Set([...available, ...missing]).size !== value.saved_security_count
        ) {
            throw new Error('Optimizer Watchlist compatibility details were invalid.');
        }
    }
    return value;
}

function requireOptimizerContextWatchlistCompatibility(value) {
    const evidence = requireOptimizerWatchlistCompatibility(value?.evidence);
    const missingCount = value?.missing_security_count;
    const restorationRequired = missingCount > 0;
    if (
        !Number.isInteger(missingCount)
        || missingCount < 0
        || missingCount !== evidence.missing_security_count
        || value?.status !== (restorationRequired ? 'restoration_required' : 'compatible')
    ) {
        throw new Error('Optimizer context Watchlist compatibility was invalid.');
    }
    return value;
}

const validOptimizerContextAction = (action) => Boolean(
    action
    && typeof action === 'object'
    && ['available', 'blocked', 'preflight_required'].includes(action.availability)
    && (
        action.availability === 'blocked'
            ? typeof action.blocker?.message === 'string'
            : action.blocker === null
    )
);

function requireOptimizerContextStatusV1(value, portfolio) {
    const parentContextIds = portfolio?.baseline_source_mode === 'actual'
        ? ['watchlist_actual_portfolio', 'actual_portfolio']
        : ['position_sizing'];
    const actionNames = [
        'refresh_current_context',
        'view_saved_run',
        'replay_saved_inputs',
    ];
    const actions = value?.actions;
    if (
        value?.contract !== OPTIMIZER_CONTEXT_STATUS_V1_CONTRACT
        || value?.context_policy_contract !== 'application-context-policy.v1'
        || value?.context_id !== 'optimized_portfolio'
        || value?.instance_id !== String(portfolio?.id)
        || !parentContextIds.includes(value?.parent_context_id)
        || value?.baseline_source_mode !== portfolio?.baseline_source_mode
        || ![
            'saved_context_available',
            'update_available',
            'modified',
            'restoration_required',
        ]
            .includes(value?.status)
        || !Array.isArray(value?.reasons)
        || value.reasons.some((reason) => !requiredText(reason, 'Optimizer context reason'))
        || (value?.parent_freshness != null
            && !['current', 'stale', 'unknown'].includes(value.parent_freshness))
        || (value?.materialization_state != null
            && !['pristine', 'modified'].includes(value.materialization_state))
        || !value?.saved_context
        || typeof value.saved_context !== 'object'
        || Array.isArray(value.saved_context)
        || value?.receipt?.receipt_id !== portfolio?.receipt_id
        || value?.receipt?.contract !== portfolio?.receipt_contract_version
        || !actions
        || typeof actions !== 'object'
        || actionNames.some((name) => !validOptimizerContextAction(actions?.[name]))
    ) {
        throw new Error('Optimizer context status was invalid.');
    }
    return value;
}

function requireOptimizerContextStatusV2(value, portfolio) {
    const parentContextIds = portfolio?.baseline_source_mode === 'actual'
        ? ['actual_portfolio', 'watchlist_actual_portfolio']
        : ['position_sizing'];
    const artifact = value?.artifact;
    const comparison = value?.current_comparison;
    const compatibility = value?.watchlist_compatibility;
    const actions = value?.actions;
    const requiredActions = [
        'view_saved_run',
        'replay_saved_inputs',
        'refresh_current_context',
        'install_in_current_workspace',
        'restore_missing_securities',
    ];
    let compatibilityValid = true;
    try {
        requireOptimizerContextWatchlistCompatibility(compatibility);
    } catch (_error) {
        compatibilityValid = false;
    }
    if (
        value?.contract !== OPTIMIZER_CONTEXT_STATUS_V2_CONTRACT
        || !['application-context-policy.v1', 'application-context-policy.v2']
            .includes(value?.context_policy_contract)
        || value?.context_id !== 'optimized_portfolio'
        || value?.instance_id !== String(portfolio?.id)
        || !parentContextIds.includes(value?.parent_context_id)
        || value?.baseline_source_mode !== portfolio?.baseline_source_mode
        || ![
            'saved_context_available',
            'update_available',
            'modified',
            'restoration_required',
        ].includes(value?.status)
        || (value?.parent_freshness != null
            && !['current', 'stale', 'unknown'].includes(value.parent_freshness))
        || !Array.isArray(value?.reasons)
        || value.reasons.some((reason) => !requiredText(reason, 'Optimizer context reason'))
        || !value?.saved_context
        || typeof value.saved_context !== 'object'
        || Array.isArray(value.saved_context)
        || !artifact
        || !['available', 'unavailable'].includes(artifact.availability)
        || !['valid', 'invalid', 'unknown'].includes(artifact.validity)
        || !['pristine', 'modified'].includes(artifact.materialization_state)
        || !comparison
        || !['known', 'unknown'].includes(comparison.availability)
        || !['current', 'update_available', 'unknown'].includes(comparison.status)
        || !Array.isArray(comparison.reasons)
        || comparison.reasons.some((reason) => !requiredText(reason, 'Optimizer context reason'))
        || !Array.isArray(comparison.changes)
        || comparison.changes.some((change) => (
            !change
            || typeof change !== 'object'
            || Array.isArray(change)
            || !requiredText(change.reason, 'Optimizer context change reason')
            || !requiredText(change.field, 'Optimizer context change field')
            || !requiredText(change.impact, 'Optimizer context change impact')
            || !Object.prototype.hasOwnProperty.call(change, 'saved')
            || !Object.prototype.hasOwnProperty.call(change, 'current')
        ))
        || !Array.isArray(comparison.compared_fields)
        || comparison.compared_fields.some((field) => !requiredText(
            field,
            'Optimizer compared field',
        ))
        || !Array.isArray(comparison.unknown_fields)
        || comparison.unknown_fields.some((field) => !requiredText(
            field,
            'Optimizer unknown field',
        ))
        || comparison.price_policy !== 'fixed_receipt_snapshot'
        || !compatibilityValid
        || !value?.receipt
        || value.receipt.receipt_id !== portfolio?.receipt_id
        || value.receipt.contract !== portfolio?.receipt_contract_version
        || !actions
        || typeof actions !== 'object'
        || requiredActions.some((name) => !validOptimizerContextAction(actions?.[name]))
    ) {
        throw new Error('Optimizer context status was invalid.');
    }
    return value;
}

function requireOptimizerContextStatus(value, portfolio) {
    if (value?.contract === OPTIMIZER_CONTEXT_STATUS_V2_CONTRACT) {
        return requireOptimizerContextStatusV2(value, portfolio);
    }
    return requireOptimizerContextStatusV1(value, portfolio);
}

export function optimizerContextStatusPresentation(value, portfolio = null) {
    const legacyCompatibility = portfolio?.compatibility ?? {};
    if (value?.contract === OPTIMIZER_CONTEXT_STATUS_V2_CONTRACT) {
        const artifact = value.artifact ?? {};
        const comparison = value.current_comparison ?? {};
        const compatibility = value.watchlist_compatibility ?? {};
        const restorationRequired = compatibility.status === 'restoration_required';
        return {
            contract: value.contract,
            parentContextId: value.parent_context_id ?? null,
            historicalAvailable: (
                artifact.availability === 'available'
                && artifact.validity === 'valid'
            ),
            artifactValidity: artifact.validity ?? 'unknown',
            materializationState: artifact.materialization_state ?? 'pristine',
            comparisonAvailability: comparison.availability ?? 'unknown',
            comparisonStatus: comparison.status ?? 'unknown',
            updateAvailable: comparison.status === 'update_available',
            comparisonUnknown: comparison.status === 'unknown',
            comparisonIncomplete: comparison.availability === 'unknown',
            reasons: Array.isArray(comparison.reasons) ? comparison.reasons : [],
            comparedFields: Array.isArray(comparison.compared_fields)
                ? comparison.compared_fields
                : [],
            unknownFields: Array.isArray(comparison.unknown_fields)
                ? comparison.unknown_fields
                : [],
            pricePolicy: comparison.price_policy ?? null,
            factorDataRelease: comparison.factor_data_release ?? null,
            changes: Array.isArray(comparison.changes) ? comparison.changes : [],
            compatibilityStatus: restorationRequired ? 'restoration_required' : 'compatible',
            restorationRequired,
            missingSecurityCount: Number(compatibility.missing_security_count || 0),
            actions: value.actions ?? {},
        };
    }

    const reasons = Array.isArray(value?.reasons)
        ? value.reasons
        : (Array.isArray(portfolio?.stale_reasons) ? portfolio.stale_reasons : []);
    const restorationRequired = value?.status === 'restoration_required'
        || Number(legacyCompatibility?.missing_security_count || 0) > 0;
    const legacyUpdateAvailable = value?.status === 'update_available'
        || (!value && portfolio?.is_baseline_stale === true && !restorationRequired);
    const comparisonUnknown = value?.parent_freshness === 'unknown';
    return {
        contract: value?.contract ?? null,
        parentContextId: value?.parent_context_id ?? null,
        historicalAvailable: value?.actions?.view_saved_run?.availability === 'available'
            || portfolio?.receipt_status === 'available',
        artifactValidity: 'unknown',
        materializationState: value?.materialization_state
            ?? (portfolio?.modified_since_optimization ? 'modified' : 'pristine'),
        comparisonAvailability: comparisonUnknown ? 'unknown' : 'known',
        comparisonStatus: legacyUpdateAvailable
            ? 'update_available'
            : comparisonUnknown ? 'unknown' : 'current',
        updateAvailable: legacyUpdateAvailable,
        comparisonUnknown,
        comparisonIncomplete: comparisonUnknown,
        reasons,
        changes: [],
        compatibilityStatus: restorationRequired ? 'restoration_required' : 'compatible',
        restorationRequired,
        missingSecurityCount: Number(legacyCompatibility?.missing_security_count || 0),
        actions: value?.actions ?? {},
    };
}

function finiteNonnegative(value, label) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error(`${label} must be a finite nonnegative number.`);
    }
    return value;
}

function finiteNumber(value, label) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`${label} must be a finite number.`);
    }
    return value;
}

function hasExactKeys(value, keys) {
    return Boolean(
        value
        && typeof value === 'object'
        && !Array.isArray(value)
        && Object.keys(value).length === keys.length
        && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key)),
    );
}

function providerV4Matches(provider, expected = null, { allowHistoricalDeployment = false } = {}) {
    const valid = (
        hasExactKeys(provider, PROVIDER_PROVENANCE_KEYS)
        && provider?.service === 'alphamizer'
        && provider?.source_revision === OPTIMIZER_V2_PROVIDER_SOURCE
        && Object.prototype.hasOwnProperty.call(ENVIRONMENTS, provider?.environment)
        && provider?.contract_digest === OPTIMIZER_V4_PROVIDER_DIGEST
        && provider?.route === OPTIMIZER_V4_PROVIDER_ROUTE
        && provider?.model_build === 'optimizer-model.v4'
        && provider?.solver_name === 'mosek-fusion'
        && provider?.solver_version === '11.0.20'
        && provider?.constraint_schema_version === OPTIMIZER_V4_CONSTRAINT_SCHEMA
        && provider?.constraint_contract_digest === OPTIMIZER_V4_CONSTRAINT_DIGEST
        && typeof provider?.deployed_revision === 'string'
        && provider.deployed_revision === provider.deployed_revision.trim()
        && provider.deployed_revision.length > 0
        && provider?.image_digest === 'in-process'
    );
    if (!valid) return false;
    return expected == null || PROVIDER_PROVENANCE_KEYS.every(
        (key) => (
            (allowHistoricalDeployment && key === 'deployed_revision')
            || provider[key] === expected[key]
        ),
    );
}

function providerV5Matches(provider, expected = null, { allowHistoricalDeployment = false } = {}) {
    const valid = (
        hasExactKeys(provider, PROVIDER_PROVENANCE_KEYS)
        && provider?.service === 'alphamizer'
        && provider?.source_revision === OPTIMIZER_V2_PROVIDER_SOURCE
        && Object.prototype.hasOwnProperty.call(ENVIRONMENTS, provider?.environment)
        && provider?.contract_digest === OPTIMIZER_V5_PROVIDER_DIGEST
        && provider?.route === OPTIMIZER_V5_PROVIDER_ROUTE
        && provider?.model_build === 'optimizer-model.v5'
        && provider?.solver_name === 'mosek-fusion'
        && provider?.solver_version === '11.0.20'
        && provider?.constraint_schema_version === OPTIMIZER_V5_CONSTRAINT_SCHEMA
        && provider?.constraint_contract_digest === OPTIMIZER_V5_CONSTRAINT_DIGEST
        && typeof provider?.deployed_revision === 'string'
        && provider.deployed_revision === provider.deployed_revision.trim()
        && provider.deployed_revision.length > 0
        && provider?.image_digest === 'in-process'
    );
    if (!valid) return false;
    return expected == null || PROVIDER_PROVENANCE_KEYS.every(
        (key) => (
            (allowHistoricalDeployment && key === 'deployed_revision')
            || provider[key] === expected[key]
        ),
    );
}

function providerV6Matches(provider, expected = null, { allowHistoricalDeployment = false } = {}) {
    const valid = hasExactKeys(provider, PROVIDER_PROVENANCE_KEYS)
        && provider.service === 'alphamizer'
        && provider.source_revision === OPTIMIZER_V2_PROVIDER_SOURCE
        && Object.prototype.hasOwnProperty.call(ENVIRONMENTS, provider.environment)
        && provider.contract_digest === '0177a642e632dd2edd30efbc5663fac254504ceade79049f805f81d49a84f689'
        && provider.route === 'in-process:optimizer-design-v1'
        && provider.model_build === 'optimizer-portfolio-design.v1'
        && provider.solver_name === 'mosek-fusion' && provider.solver_version === '11.0.20'
        && provider.constraint_schema_version === 'optimizer-constraints.v6'
        && provider.constraint_contract_digest === '0719998052979a30d822a39c5469d45c3eecc9fd2f0e95767b93673614af8d9a'
        && typeof provider.deployed_revision === 'string' && provider.deployed_revision.trim() === provider.deployed_revision && provider.deployed_revision.length > 0
        && provider.image_digest === 'in-process';
    return valid && (expected == null || PROVIDER_PROVENANCE_KEYS.every((key) => (allowHistoricalDeployment && key === 'deployed_revision') || provider[key] === expected[key]));
}

function providerV7Matches(provider, expected = null, { allowHistoricalDeployment = false } = {}) {
    const valid = hasExactKeys(provider, PROVIDER_PROVENANCE_KEYS)
        && provider.service === 'alphamizer'
        && provider.source_revision === OPTIMIZER_V2_PROVIDER_SOURCE
        && Object.prototype.hasOwnProperty.call(ENVIRONMENTS, provider.environment)
        && provider.contract_digest === OPTIMIZER_V7_PROVIDER_DIGEST
        && provider.route === OPTIMIZER_V7_PROVIDER_ROUTE
        && provider.model_build === OPTIMIZER_V7_PROVIDER_MODEL_BUILD
        && provider.solver_name === 'mosek-fusion' && provider.solver_version === '11.0.20'
        && provider.constraint_schema_version === OPTIMIZER_V5_CONSTRAINT_SCHEMA
        && provider.constraint_contract_digest === OPTIMIZER_V5_CONSTRAINT_DIGEST
        && typeof provider.deployed_revision === 'string' && provider.deployed_revision.trim() === provider.deployed_revision && provider.deployed_revision.length > 0
        && provider.image_digest === 'in-process';
    return valid && (expected == null || PROVIDER_PROVENANCE_KEYS.every((key) => (allowHistoricalDeployment && key === 'deployed_revision') || provider[key] === expected[key]));
}

function receiptProviderMatcher(receiptContract, provider) {
    if (provider?.constraint_schema_version === 'optimizer-constraints.v6') {
        return receiptContract === OPTIMIZER_V8_RECEIPT_CONTRACT ? providerV6Matches : null;
    }
    if (provider?.constraint_schema_version === OPTIMIZER_V5_CONSTRAINT_SCHEMA) {
        if (receiptContract === OPTIMIZER_V9_RECEIPT_CONTRACT) return providerV7Matches;
        return receiptContract === OPTIMIZER_V7_RECEIPT_CONTRACT
            ? providerV5Matches
            : null;
    }
    if (provider?.constraint_schema_version === OPTIMIZER_V4_CONSTRAINT_SCHEMA) {
        return receiptContract === OPTIMIZER_V6_RECEIPT_CONTRACT
            ? providerV4Matches
            : null;
    }
    return null;
}

function validOptimizerV3SolverStatus(value, design = false) {
    const gap = value?.relative_gap;
    const elapsed = value?.solve_time_seconds;
    const numbersValid = (
        (gap === null || (typeof gap === 'number' && Number.isFinite(gap) && gap >= 0))
        && typeof elapsed === 'number'
        && Number.isFinite(elapsed)
        && elapsed >= 0
    );
    if (!numbersValid) return false;
    if (design && value.outcome === 'feasible_not_proven' && gap === null
        && ['stationary_candidate', 'iteration_limit', 'search_budget', 'numerical_stop'].includes(value.termination)) return true;
    if (value.outcome === 'optimal') {
        if (value.termination === 'continuous_nonbinding') return gap === null;
        return ['optimal', 'gap_tolerance'].includes(value.termination)
            && (gap === null || gap <= 0.001);
    }
    return value.outcome === 'feasible_not_proven'
        && ['time_limit', 'time_or_gap_limit'].includes(value.termination)
        && gap !== null
        && gap > 0.001;
}

export function requireOptimizerV2Capability(value) {
    const applicationEnvironment = value?.application_environment;
    const replayCapability = value?.saved_input_replay;
    const cardinality = value?.cardinality;
    if (
        value?.enabled !== true
        || value?.contract !== OPTIMIZER_V7_RECEIPT_CONTRACT
        || value?.constraint_schema_version !== OPTIMIZER_V5_CONSTRAINT_SCHEMA
        || value?.constraint_contract_digest !== OPTIMIZER_V5_CONSTRAINT_DIGEST
        || value?.unavailable_code !== null
        || !Object.prototype.hasOwnProperty.call(ENVIRONMENTS, applicationEnvironment)
        || value?.provider?.environment !== ENVIRONMENTS[applicationEnvironment]
        || value?.provider?.contract !== OPTIMIZER_V5_PROVIDER_CONTRACT
        || value?.provider?.execution_mode !== 'in_process'
        || !providerV5Matches(Object.fromEntries(
            PROVIDER_PROVENANCE_KEYS.map((key) => [key, value?.provider?.[key]]),
        ))
        || !Number.isInteger(value?.limits?.group_constraint_records)
        || value.limits.group_constraint_records <= 0
        || !Number.isInteger(value?.limits?.members_per_group)
        || value.limits.members_per_group <= 0
        || (replayCapability != null && (
            replayCapability?.contract !== OPTIMIZER_SAVED_INPUT_REPLAY_CONTRACT
            || typeof replayCapability?.enabled !== 'boolean'
        ))
        || (cardinality != null && (
            typeof cardinality?.enabled !== 'boolean'
            || !Array.isArray(cardinality?.objectives)
            || cardinality.objectives.join(',') !== 'AlphaScore'
            || !Number.isFinite(cardinality?.mio_max_time_seconds)
            || !Number.isFinite(cardinality?.mio_relative_gap)
            || (cardinality.enabled && applicationEnvironment !== 'dev')
        ))
    ) {
        throw new Error('Generic Optimizer constraints are unavailable. Refresh and retry.');
    }
    return value;
}

export async function preflightOptimizerV2Capability() {
    return requireOptimizerV2Capability(
        await callBackend('optimizer_generic_group_capabilities', 'GET'),
    );
}

export function requireOptimizerGroupCatalog(value) {
    const effectiveV3 = value?.attribute_contract_version === 3;
    if (
        value?.contract !== 'optimizer-group-catalog.v1'
        || (
            Object.prototype.hasOwnProperty.call(value ?? {}, 'attribute_contract_version')
            && !effectiveV3
        )
        || !UUID_RE.test(value?.revision_id ?? '')
        || !Number.isInteger(value?.revision_seq)
        || !SHA_RE.test(value?.state_hash ?? '')
        || !SHA_RE.test(value?.definition_fingerprint ?? '')
        || !SHA_RE.test(value?.content_fingerprint ?? '')
        || typeof value?.pointer_version !== 'string'
        || !value.pointer_version
        || !Array.isArray(value?.definitions)
    ) {
        throw new Error('Optimizer attribute catalog is invalid.');
    }
    const ids = new Set();
    value.definitions.forEach((definition) => {
        const id = definition?.attribute_definition_id;
        if (
            !hasExactKeys(definition, [
                'attribute_definition_id',
                'display_name',
                'display_name_key',
                'display_order',
                'source_kind',
                'editable',
                ...(effectiveV3 ? ['system_key'] : []),
                'groups',
            ])
            || !UUID_RE.test(id ?? '')
            || ids.has(id)
            || !requiredText(definition?.display_name, 'Attribute name')
            || !requiredText(definition?.display_name_key, 'Attribute name key')
            || !Number.isInteger(definition?.display_order)
            || definition.display_order < 0
            || !['user', 'system_derived'].includes(definition?.source_kind)
            || typeof definition?.editable !== 'boolean'
            || (
                effectiveV3
                && (
                    (definition.source_kind === 'system_derived'
                        && definition.system_key !== 'country')
                    || (definition.source_kind === 'user' && definition.system_key !== null)
                )
            )
            || !Array.isArray(definition?.groups)
        ) {
            throw new Error('Optimizer attribute catalog definition is invalid.');
        }
        ids.add(id);
        const groupKeys = new Set();
        definition.groups.forEach((group) => {
            const identity = JSON.stringify([group?.group_kind, group?.group_value_key ?? null]);
            if (
                !hasExactKeys(group, ['group_kind', 'group_value', 'group_value_key'])
                || group?.group_kind !== 'assigned'
                || !requiredText(group?.group_value, 'Attribute category')
                || !requiredText(group?.group_value_key, 'Attribute category key')
                || Object.prototype.hasOwnProperty.call(group, 'member_cognive_ids')
                || groupKeys.has(identity)
            ) {
                throw new Error('Optimizer attribute category is invalid.');
            }
            groupKeys.add(identity);
        });
    });
    return value;
}

export async function fetchOptimizerGroupCatalog() {
    return requireOptimizerGroupCatalog(
        await callBackend('optimizer_generic_group_catalog', 'GET'),
    );
}

let optimizerContractCache = null;
let optimizerContractInFlight = null;

export function clearOptimizerContractCache({ preserveUserUid } = {}) {
    const preservedUser = String(preserveUserUid ?? '').trim();
    const retainedEntries = [optimizerContractCache, optimizerContractInFlight]
        .filter(Boolean);
    if (
        preservedUser
        && retainedEntries.length
        && retainedEntries.every((entry) => entry.userUid === preservedUser)
    ) {
        return;
    }
    optimizerContractCache = null;
    optimizerContractInFlight = null;
}

export async function getOrLoadOptimizerContract({
    userUid,
    watchlistVersion,
    force = false,
} = {}) {
    const normalizedUserUid = String(userUid ?? '').trim();
    if (!normalizedUserUid) {
        throw new Error('Optimizer user scope is unavailable.');
    }
    const dependencyKey = JSON.stringify([
        normalizedUserUid,
        Number(watchlistVersion ?? 0),
    ]);
    if (!force) {
        if (optimizerContractCache?.dependencyKey === dependencyKey) {
            return optimizerContractCache.value;
        }
        if (optimizerContractInFlight?.dependencyKey === dependencyKey) {
            return optimizerContractInFlight.promise;
        }
    }
    optimizerContractCache = null;
    optimizerContractInFlight = null;
    const pending = Promise.all([
        preflightOptimizerV2Capability(),
        fetchOptimizerGroupCatalog(),
    ]).then(([capability, catalog]) => {
        const identity = JSON.stringify([
            normalizedUserUid,
            catalog.revision_id,
            catalog.definition_fingerprint,
            catalog.content_fingerprint,
        ]);
        const resolved = { capability, catalog, identity };
        if (optimizerContractInFlight?.promise === pending) {
            optimizerContractCache = {
                userUid: normalizedUserUid,
                dependencyKey,
                value: resolved,
            };
        }
        return resolved;
    }).finally(() => {
        if (optimizerContractInFlight?.promise === pending) {
            optimizerContractInFlight = null;
        }
    });
    optimizerContractInFlight = {
        userUid: normalizedUserUid,
        dependencyKey,
        promise: pending,
    };
    return pending;
}

export function optimizerGroupIntentKey(record) {
    return JSON.stringify([
        requiredText(record?.attribute_definition_id, 'Attribute definition'),
        record?.group_kind,
        record?.group_value_key ?? null,
        requiredText(record?.name, 'Group metric'),
    ]);
}

export function buildOptimizerV2ConstraintDocument({
    constraints,
    constraintToggles,
    groupConstraints,
}) {
    const legacy = buildOptimizerConstraintDocument({ constraints, constraintToggles });
    const staticConstraints = legacy.static.map((record) => (
        record.name === 'gross_market_value_musd'
            ? { ...record, enabled: true }
            : record
    ));
    if (!Array.isArray(groupConstraints)) {
        throw new Error('Generic Optimizer constraint state is invalid.');
    }
    const seen = new Set();
    const group = groupConstraints.map((record) => {
        const definitionId = requiredText(
            record?.attribute_definition_id,
            'Attribute definition',
        );
        if (!UUID_RE.test(definitionId)) {
            throw new Error('Attribute definition is invalid.');
        }
        const kind = record?.group_kind;
        const key = record?.group_value_key ?? null;
        const name = requiredText(record?.name, 'Generic group metric');
        if (
            !['assigned', 'unassigned'].includes(kind)
            || (kind === 'assigned' && !requiredText(key, 'Attribute category'))
            || (kind === 'unassigned' && key !== null)
            || !OPTIMIZER_GROUP_METRICS.some(({ value }) => value === name)
            || typeof record?.enabled !== 'boolean'
        ) {
            throw new Error('Generic Optimizer group constraint is invalid.');
        }
        const normalized = {
            attribute_definition_id: definitionId,
            group_kind: kind,
            group_value_key: key,
            name,
            enabled: record.enabled,
            value: finiteNonnegative(record.value, 'Generic group constraint'),
        };
        const identity = optimizerGroupIntentKey(normalized);
        if (seen.has(identity)) {
            throw new Error('Generic Optimizer group constraint is duplicated.');
        }
        seen.add(identity);
        return normalized;
    }).sort((left, right) => (
        optimizerGroupIntentKey(left).localeCompare(optimizerGroupIntentKey(right))
    ));
    return { static: staticConstraints, group, factor: legacy.factor };
}

export function buildOptimizerV4ConstraintDocument(options) {
    const v2 = buildOptimizerV2ConstraintDocument(options);
    const enabled = options?.constraintToggles?.static_constraints
        ?.max_names_changed === true;
    if (!enabled) return { ...v2, cardinality: [] };
    const value = Number(options?.constraints?.static_constraints?.max_names_changed);
    if (!Number.isInteger(value) || value < 0) {
        throw new Error('Maximum names changed must be a nonnegative integer.');
    }
    return {
        ...v2,
        cardinality: [{ name: 'max_names_changed', enabled: true, value }],
    };
}

// A disabled rule keeps its limit; a cleared limit is sent as 0 while it is off.
export function buildOptimizerV5ConstraintDocument({ selectedCogniveIds, ...options }) {
    const v4 = buildOptimizerV4ConstraintDocument(options);
    const values = options?.constraints?.static_constraints ?? {};
    const toggles = options?.constraintToggles?.static_constraints ?? {};
    const trading = OPTIMIZER_TRADING_RULES.map(({ name, field }) => {
        const enabled = toggles[field] === true;
        const value = values[field];
        return {
            name,
            enabled,
            value: enabled || value != null
                ? finiteNonnegative(value, `Trading limit ${field}`)
                : 0,
        };
    });
    const selected = new Set(selectedCogniveIds ?? []);
    const controls = options?.constraints?.security_controls ?? {};
    if (!controls || typeof controls !== 'object' || Array.isArray(controls)) {
        throw new Error('Optimizer security controls are invalid.');
    }
    // Only checked names are run; a control on an unchecked name is kept for
    // later but not sent.
    const securityControls = Object.entries(controls)
        .filter(([cogniveId, control]) => (
            selected.has(cogniveId) && OPTIMIZER_SECURITY_CONTROLS.includes(control)
        ))
        .map(([cogniveId, control]) => ({ cognive_id: cogniveId, control }))
        .sort((left, right) => (left.cognive_id < right.cognive_id ? -1 : 1));
    return { ...v4, trading, security_controls: securityControls };
}

// Trading limits and per-name controls from a saved v5 document, or the
// switched-off defaults for an earlier one.
function decodeSavedTradingRules(document) {
    const trading = document.trading;
    const controls = document.security_controls;
    if (trading === undefined && controls === undefined) {
        return {
            values: Object.fromEntries(OPTIMIZER_TRADING_RULES.map(
                ({ field, defaultValue }) => [field, defaultValue],
            )),
            toggles: Object.fromEntries(OPTIMIZER_TRADING_RULES.map(
                ({ field }) => [field, false],
            )),
            securityControls: {},
        };
    }
    if (
        !Array.isArray(trading)
        || trading.length !== OPTIMIZER_TRADING_RULES.length
        || OPTIMIZER_TRADING_RULES.some(({ name }, index) => (
            !hasExactKeys(trading[index], ['name', 'enabled', 'value'])
            || trading[index].name !== name
            || typeof trading[index].enabled !== 'boolean'
        ))
        || !Array.isArray(controls)
        || controls.some((record) => (
            !hasExactKeys(record, ['cognive_id', 'control'])
            || !requiredText(record.cognive_id, 'Saved security control')
            || !OPTIMIZER_SECURITY_CONTROLS.includes(record.control)
        ))
    ) {
        throw new Error('Saved Optimizer trading rules are invalid.');
    }
    const ids = controls.map((record) => record.cognive_id);
    if (new Set(ids).size !== ids.length || ids.join('|') !== [...ids].sort().join('|')) {
        throw new Error('Saved Optimizer security controls are not canonical.');
    }
    return {
        values: Object.fromEntries(OPTIMIZER_TRADING_RULES.map(({ field }, index) => [
            field,
            finiteNonnegative(trading[index].value, `Saved trading limit ${field}`),
        ])),
        toggles: Object.fromEntries(OPTIMIZER_TRADING_RULES.map(({ field }, index) => [
            field,
            trading[index].enabled,
        ])),
        securityControls: Object.fromEntries(
            controls.map((record) => [record.cognive_id, record.control]),
        ),
    };
}

function normalizeSavedGroupIntent(record) {
    if (
        !record
        || typeof record !== 'object'
        || Array.isArray(record)
        || !['assigned', 'unassigned'].includes(record.group_kind)
        || (record.group_kind === 'assigned'
            && !requiredText(record.group_value_key, 'Saved group category'))
        || (record.group_kind === 'unassigned' && record.group_value_key !== null)
        || !OPTIMIZER_GROUP_METRICS.some(({ value }) => value === record.name)
        || typeof record.enabled !== 'boolean'
        || !UUID_RE.test(record.attribute_definition_id ?? '')
    ) {
        throw new Error('Saved generic Optimizer constraint is invalid.');
    }
    return {
        attribute_definition_id: record.attribute_definition_id,
        group_kind: record.group_kind,
        group_value_key: record.group_value_key,
        name: record.name,
        enabled: record.enabled,
        value: finiteNonnegative(record.value, 'Saved generic group constraint'),
    };
}

export function decodeOptimizerV2ConstraintDocument(document) {
    if (
        !document
        || typeof document !== 'object'
        || Array.isArray(document)
        || !Array.isArray(document.static)
        || !Array.isArray(document.group)
        || !Array.isArray(document.factor)
    ) {
        throw new Error('Saved generic Optimizer constraint document is invalid.');
    }
    const decoded = decodeOptimizerConstraintDocument({
        static: document.static,
        factor: document.factor,
    });
    const groupConstraints = document.group.map(normalizeSavedGroupIntent);
    const identities = groupConstraints.map(optimizerGroupIntentKey);
    if (
        new Set(identities).size !== identities.length
        || identities.join('|') !== [...identities].sort().join('|')
    ) {
        throw new Error('Saved generic Optimizer constraints are not canonical.');
    }
    const cardinality = document.cardinality ?? [];
    if (
        !Array.isArray(cardinality)
        || cardinality.length > 1
        || (cardinality.length === 1 && (
            cardinality[0]?.name !== 'max_names_changed'
            || cardinality[0]?.enabled !== true
            || !Number.isInteger(cardinality[0]?.value)
            || cardinality[0].value < 0
        ))
    ) {
        throw new Error('Saved cardinality constraint is invalid.');
    }
    const trading = decodeSavedTradingRules(document);
    return {
        ...decoded,
        constraints: {
            ...decoded.constraints,
            static_constraints: {
                ...decoded.constraints.static_constraints,
                ...trading.values,
                ...(cardinality.length ? { max_names_changed: cardinality[0].value } : {}),
            },
            security_controls: trading.securityControls,
        },
        constraintToggles: {
            ...decoded.constraintToggles,
            static_constraints: {
                ...decoded.constraintToggles.static_constraints,
                ...trading.toggles,
                ...(cardinality.length ? { max_names_changed: true } : {}),
            },
        },
        groupConstraints,
    };
}

export function optimizerV2VersionCommand(fields = {}) {
    return canonicalJsonObject({
        contract_version: OPTIMIZER_V2_RECEIPT_CONTRACT,
        constraint_schema_version: OPTIMIZER_V2_CONSTRAINT_SCHEMA,
        ...fields,
    });
}

export function requireOptimizerV2RunResponse(value, expected = null) {
    const result = value?.result;
    const receipt = value?.receipt;
    const receiptContract = value?.contract;
    const solverStatus = value?.solver_status;
    const expectedWatchlistContract = 'watchlist-attribute-target-snapshot.v3';
    const matchesProvider = receiptProviderMatcher(receiptContract, value?.provider);
    if (!matchesProvider || !matchesProvider(
        value?.provider,
        expected?.provider,
        { allowHistoricalDeployment: expected?.allowHistoricalDeployment === true },
    )) {
        throw new Error('Optimizer provider provenance was incompatible.');
    }
    if (
        !OPTIMIZER_SAVED_RECEIPT_CONTRACTS.includes(receiptContract)
        || value?.status !== 'success'
        || !UUID4_RE.test(receipt?.receipt_id ?? '')
        || !UUID4_RE.test(receipt?.run_operation_id ?? '')
        || !SHA_RE.test(receipt?.receipt_sha256 ?? '')
        || !requiredText(receipt?.issued_at, 'Optimizer receipt issued time')
        || !requiredText(receipt?.first_save_expires_at, 'Optimizer receipt expiry')
        || result?.contract !== OPTIMIZER_RUN_RESULT_CONTRACT
        || result?.status !== 'success'
        || !Array.isArray(result?.securities)
        || !Array.isArray(result?.risks_by_factor)
        || !hasExactKeys(result?.risk_metrics, RISK_METRIC_KEYS)
        || RISK_METRIC_KEYS.some((key) => result.risk_metrics[key] == null)
        || value?.watchlist_attributes?.contract !== expectedWatchlistContract
        || (expected?.runOperationId && receipt.run_operation_id !== expected.runOperationId)
        || (expected?.objective && result.objective !== expected.objective)
        || !validOptimizerV3SolverStatus(solverStatus, receiptContract === OPTIMIZER_V8_RECEIPT_CONTRACT)
    ) {
        throw new Error('Optimizer v2 receipt response was incomplete.');
    }
    const allocations = {};
    const securityRows = result.securities.map((row) => {
        if (!hasExactKeys(row, RESULT_SECURITY_KEYS)) {
            throw new Error('Optimizer v2 security row was invalid.');
        }
        const cogniveId = requiredText(row?.cognive_id, 'Optimizer security');
        if (Object.prototype.hasOwnProperty.call(allocations, cogniveId)) {
            throw new Error('Optimizer v2 security scope is duplicated.');
        }
        allocations[cogniveId] = finiteNumber(
            row.gmv_optimized,
            `${cogniveId} optimized GMV`,
        );
        return {
            beta: finiteNumber(row.beta, `${cogniveId} beta`),
            cognive_id: cogniveId,
            expected_return: finiteNumber(
                row.expected_return,
                `${cogniveId} expected return`,
            ),
            gmv_actual: finiteNumber(row.gmv_actual, `${cogniveId} actual GMV`),
            gmv_optimized: allocations[cogniveId],
            score: finiteNumber(row.score, `${cogniveId} score`),
            ticker: requiredText(row.ticker, `${cogniveId} ticker`),
        };
    });
    if (!securityRows.length) {
        throw new Error('Optimizer v2 security scope is empty.');
    }
    const attributeRiskMetrics = requireOptimizerAttributeRiskMetrics(
        value.attribute_risk_metrics,
    );
    const constraintEvaluation = requireOptimizerConstraintEvaluation(
        value.constraint_evaluation,
        receipt.receipt_id,
    );
    return {
        ...value,
        allocations,
        securityRows,
        attributeRiskMetrics,
        constraintEvaluation,
    };
}

const nullableFiniteNumber = (value) => (
    value === null || (typeof value === 'number' && Number.isFinite(value))
);

// The evaluation is a display projection bound to one receipt. An older backend
// omits it; a present projection must belong to the same receipt.
export function requireOptimizerConstraintEvaluation(value, receiptId) {
    if (value == null) return null;
    const rules = value?.rules;
    if (
        value?.contract !== OPTIMIZER_CONSTRAINT_EVALUATION_CONTRACT
        || value?.receipt_id !== receiptId
        || !['actual', 'pos_sizing'].includes(value?.baseline_source_mode)
        || !Number.isFinite(value?.inputs?.aum_usd)
        || !Number.isFinite(value?.inputs?.gmv_budget_usd)
        || !value?.portfolio
        || typeof value.portfolio !== 'object'
        || !Array.isArray(rules)
        || rules.some((rule) => (
            !rule
            || typeof rule !== 'object'
            || typeof rule.rule_id !== 'string'
            || !rule.rule_id
            || !CONSTRAINT_EVALUATION_SCOPES.includes(rule.scope)
            || !CONSTRAINT_EVALUATION_FORMS.includes(rule.form)
            || !CONSTRAINT_EVALUATION_STATUSES.includes(rule.status)
            || typeof rule.configured !== 'boolean'
            || typeof rule.enabled !== 'boolean'
            || typeof rule.applied !== 'boolean'
            || ![rule.limit, rule.baseline, rule.optimized, rule.slack]
                .every(nullableFiniteNumber)
            || !rule.identity
            || typeof rule.identity !== 'object'
        ))
        || new Set(rules.map((rule) => rule.rule_id)).size !== rules.length
    ) {
        throw new Error('Optimizer constraint evaluation was invalid.');
    }
    return value;
}

export function requireOptimizerAttributeRiskMetrics(value) {
    const actual = value?.actual;
    const optimized = value?.optimized;
    const payloads = [actual, optimized];
    if (
        value?.contract !== OPTIMIZER_ATTRIBUTE_RISK_RESULTS_CONTRACT
        || payloads.some((payload) => (
            payload?.contract !== 'risk-overview-attribute-groups.v1'
            || payload?.success !== true
            || !Array.isArray(payload?.attribute_definitions)
            || !Array.isArray(payload?.attribute_results)
            || typeof payload?.watchlist_revision_id !== 'string'
            || !payload.watchlist_revision_id
            || typeof payload?.attribute_definition_fingerprint !== 'string'
            || !payload.attribute_definition_fingerprint
            || typeof payload?.attribute_content_fingerprint !== 'string'
            || !payload.attribute_content_fingerprint
        ))
        || [
            'watchlist_revision_id',
            'attribute_definition_fingerprint',
            'attribute_content_fingerprint',
        ].some((field) => actual[field] !== optimized[field])
    ) {
        throw new Error('Optimizer attribute risk results were invalid.');
    }
    return value;
}

function requireOptimizerV2PortfolioEnvelope(value) {
    if (
        value?.contract !== OPTIMIZER_V2_PORTFOLIO_PROJECTION
        || value?.run_contract !== OPTIMIZER_V2_RECEIPT_CONTRACT
        || value?.constraint_schema_version !== OPTIMIZER_V2_CONSTRAINT_SCHEMA
    ) {
        throw new Error('Optimizer v2 portfolio response was invalid.');
    }
    return value;
}

export function requireOptimizerV2PortfolioSummariesResponse(value) {
    requireOptimizerV2PortfolioEnvelope(value);
    if (
        !Array.isArray(value.portfolios)
        || value.portfolios.some((portfolio) => (
            !portfolio
            || typeof portfolio !== 'object'
            || !Number.isInteger(portfolio.id)
            || portfolio.id <= 0
            || !['opt', 'wi'].includes(portfolio.type)
            || !requiredText(portfolio.name, 'Optimizer portfolio name')
            || Object.prototype.hasOwnProperty.call(portfolio, 'constraint_document')
            || Object.prototype.hasOwnProperty.call(portfolio, 'positions')
            || (portfolio.type === 'wi' && portfolio.receipt_status !== 'not_applicable')
            || (portfolio.type === 'opt' && !['available', 'unavailable'].includes(portfolio.receipt_status))
            || (portfolio.type === 'opt' && portfolio.receipt_status === 'unavailable'
                && portfolio.read_only !== true)
            || (portfolio.type === 'opt' && portfolio.receipt_status === 'available' && (() => {
                try {
                    requireOptimizerWatchlistCompatibility(portfolio.compatibility);
                    if (portfolio.context_status != null) {
                        requireOptimizerContextStatus(portfolio.context_status, portfolio);
                    }
                    return false;
                } catch (_error) {
                    return true;
                }
            })())
            || (portfolio.type === 'opt'
                && portfolio.receipt_status === 'available'
                && (
                    portfolio.read_only !== false
                    || !UUID4_RE.test(portfolio.receipt_id ?? '')
                    || !OPTIMIZER_SAVED_RECEIPT_CONTRACTS
                        .includes(portfolio.receipt_contract_version)
                    || !Number.isInteger(portfolio.portfolio_version)
                    || !Number.isInteger(portfolio.last_optimizer_portfolio_version)
                    || typeof portfolio.modified_since_optimization !== 'boolean'
                    || typeof portfolio.is_baseline_stale !== 'boolean'
                    || !Array.isArray(portfolio.stale_reasons)
                ))
        ))
        || new Set(value.portfolios.map(({ id }) => id)).size !== value.portfolios.length
    ) {
        throw new Error('Optimizer v2 portfolio summaries were invalid.');
    }
    return value.portfolios;
}

export function requireOptimizerV2PortfolioDetailResponse(value, expected = null) {
    requireOptimizerV2PortfolioEnvelope(value);
    const portfolio = value?.portfolio;
    const applicationEnvironment = value?.application_environment;
    if (
        value?.status !== 'success'
        || !portfolio
        || !Number.isInteger(portfolio.id)
        || portfolio.id <= 0
        || portfolio.type !== 'opt'
        || (applicationEnvironment != null && (
            !Object.prototype.hasOwnProperty.call(ENVIRONMENTS, applicationEnvironment)
            || value?.provider?.environment !== ENVIRONMENTS[applicationEnvironment]
        ))
        || (expected?.portfolioId != null && portfolio.id !== expected.portfolioId)
    ) {
        throw new Error('Optimizer v2 portfolio detail was invalid.');
    }
    requireOptimizerWatchlistCompatibility(value.compatibility, { details: true });
    if (
        value.receipt_status !== 'available'
        || !OPTIMIZER_SAVED_RECEIPT_CONTRACTS.includes(value.source_receipt_contract)
        || portfolio.receipt_contract_version !== value.source_receipt_contract
        || !Number.isInteger(portfolio.portfolio_version)
        || portfolio.portfolio_version <= 0
        || !Number.isInteger(portfolio.last_optimizer_portfolio_version)
        || portfolio.last_optimizer_portfolio_version <= 0
        || portfolio.last_optimizer_portfolio_version > portfolio.portfolio_version
        || typeof portfolio.modified_since_optimization !== 'boolean'
        || portfolio.modified_since_optimization !== (
            portfolio.portfolio_version > portfolio.last_optimizer_portfolio_version
        )
        || !['actual', 'pos_sizing'].includes(portfolio.baseline_source_mode)
        || (expected?.portfolioVersion != null
            && portfolio.portfolio_version !== expected.portfolioVersion)
        || !Array.isArray(value.current_positions)
        || value.current_positions.some((position) => (
            !hasExactKeys(position, CURRENT_POSITION_KEYS)
            || !requiredText(position.cognive_id, 'Optimizer current position')
            || (position.ticker !== null
                && !requiredText(position.ticker, 'Optimizer current ticker'))
            || !Number.isFinite(position.quantity)
            || !Number.isFinite(position.gmv)
        ))
    ) {
        throw new Error('Optimizer v2 portfolio detail receipt was unavailable.');
    }
    const receiptResult = requireOptimizerV2RunResponse({
        contract: value.source_receipt_contract,
        status: value.status,
        receipt: value.receipt,
        provider: value.provider,
        result: value.result,
        watchlist_attributes: value.watchlist_attributes,
        attribute_risk_metrics: value.attribute_risk_metrics,
        constraint_evaluation: value.constraint_evaluation,
        solver_status: value.solver_status,
    }, {
        provider: expected?.provider,
        // The immutable receipt records the revision that produced it. A routine
        // application deployment must not make that historical receipt unloadable.
        allowHistoricalDeployment: true,
    });
    const currentById = new Map(
        value.current_positions.map((position) => [position.cognive_id, position]),
    );
    if (
        value.current_positions.some((position) => (
            !Object.prototype.hasOwnProperty.call(
                receiptResult.allocations,
                position.cognive_id,
            )
        ))
        || currentById.size !== value.current_positions.length
    ) {
        throw new Error('Optimizer v2 current portfolio scope was invalid.');
    }
    const availableIds = new Set(value.compatibility.available_cognive_ids);
    const receiptIds = receiptResult.securityRows
        .map((row) => row.cognive_id)
        .sort();
    const compatibilityIds = [
        ...value.compatibility.available_cognive_ids,
        ...value.compatibility.missing_cognive_ids,
    ].sort();
    if (
        receiptIds.length !== compatibilityIds.length
        || receiptIds.some((id, index) => id !== compatibilityIds[index])
    ) {
        throw new Error('Optimizer Watchlist compatibility scope was invalid.');
    }
    const securityRows = receiptResult.securityRows
        .filter((row) => availableIds.has(row.cognive_id));
    return {
        ...value,
        allocations: Object.fromEntries(
            securityRows.map((row) => [row.cognive_id, row.gmv_optimized]),
        ),
        securityRows,
        constraintEvaluation: receiptResult.constraintEvaluation,
    };
}

export function requireOptimizerV2SaveResponse(value, expected = null) {
    requireOptimizerV2PortfolioEnvelope(value);
    if (
        value?.status !== 'success'
        || !UUID4_RE.test(value?.receipt_id ?? '')
        || !UUID4_RE.test(value?.save_operation_id ?? '')
        || !Number.isInteger(value?.portfolio_id)
        || !Number.isInteger(value?.committed_portfolio_version)
        || value.portfolio_id <= 0
        || value.committed_portfolio_version <= 0
        || (expected && (
            value.receipt_id !== expected.receipt_id
            || value.save_operation_id !== expected.save_operation_id
            || value.action !== expected.action
            || value.committed_portfolio_version !== expected.expected_portfolio_version + 1
            || (expected.action === 'update'
                && value.portfolio_id !== expected.target_portfolio_id)
        ))
    ) {
        throw new Error('Optimizer v2 save response was invalid.');
    }
    return value;
}

export async function fetchOptimizerV2PortfolioDetail(
    portfolioId,
    portfolioVersion,
) {
    return requireOptimizerV2PortfolioDetailResponse(await callBackend(
        'load_optimizer_portfolio_v2',
        'POST',
        optimizerV2VersionCommand({
            expected_portfolio_version: portfolioVersion,
            portfolio_id: portfolioId,
        }),
    ), {
        portfolioId,
        portfolioVersion,
    });
}

export function requireOptimizerSavedInputReplayResponse(value, expected = null) {
    const replayProviderMatches = receiptProviderMatcher(
        value?.receipt_contract,
        value?.saved_provider,
    );
    if (
        value?.contract !== OPTIMIZER_SAVED_INPUT_REPLAY_CONTRACT
        || !['matched', 'different', 'failed'].includes(value?.status)
        || !UUID4_RE.test(value?.receipt_id ?? '')
        || !OPTIMIZER_SAVED_RECEIPT_CONTRACTS.includes(value?.receipt_contract)
        || typeof value?.matches_saved_result !== 'boolean'
        || !requiredText(value?.saved_run_issued_at, 'Saved run issue time')
        || !replayProviderMatches
        || !replayProviderMatches(
            value?.saved_provider,
            null,
            { allowHistoricalDeployment: true },
        )
        || !replayProviderMatches(value?.replay_provider)
        || (value.status === 'matched' && value.matches_saved_result !== true)
        || (value.status !== 'matched' && value.matches_saved_result !== false)
        || (value.status === 'failed') !== Boolean(value?.failure)
        || (value?.failure && (
            !requiredText(value.failure.code, 'Replay failure code')
            || !requiredText(value.failure.message, 'Replay failure message')
        ))
        || (expected?.receiptId && value.receipt_id !== expected.receiptId)
    ) {
        throw new Error('Optimizer saved-input replay response was invalid.');
    }
    return value;
}

export async function replayOptimizerSavedInputs(portfolio) {
    const capability = await preflightOptimizerV2Capability();
    if (capability?.saved_input_replay?.enabled !== true) {
        throw new Error('Saved-input replay is unavailable. Refresh and retry.');
    }
    const command = canonicalJsonObject({
        contract: OPTIMIZER_SAVED_INPUT_REPLAY_CONTRACT,
        expected_portfolio_version: portfolio?.portfolioVersion,
        portfolio_id: portfolio?.id,
    });
    return requireOptimizerSavedInputReplayResponse(await runOptimizerWithQueue(command, {
        endpoint: 'replay_optimizer_saved_inputs_v1',
    }), {
        receiptId: portfolio?.receiptId,
    });
}
